/* eslint-disable max-len */
import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import { PromptExecutionSettingsDto } from '../../api-service/clients';
import { IChatMessage, ISummaPreview, useChat } from '../../hooks/ai-search/use-chat';
import { ChatMessages } from './chat-messages';
import { ChatMessageInput } from './chat-message-input';
import { AssistantType } from './assistant-type';
import { useCitizenContext } from '../../areas/citizen/citizen-context-hooks';
import { useChatErrorHandling } from './hooks/use-chat-error-handling';
import { useClearChatOnCitizenChange } from './hooks/use-clear-chat-on-citizen-change';
import { chatComplianceBoxPropsSumma } from './chat-compliance-box-props-summa';
import { Checkbox } from '../checkbox';
import { useSummaPreview } from './hooks/use-summa-preview';
import { useShowDrafts } from './hooks/use-show-drafts';
import { SummaPreviewBanner } from './summa-preview';
import { SummaSettings } from './summa-settings';

export interface ICitizenAssistantChatboxProps {
  settings: PromptExecutionSettingsDto;
  citizenId?: string;
  onSettingsChange: (property: keyof PromptExecutionSettingsDto, value: any) => void;
}

export function CitizenAssistantChatbox(props: ICitizenAssistantChatboxProps) {
  const { citizenId, settings, onSettingsChange } = props;
  const { mayTryPreview, offeredPreview, chosenPreview, choosePreview, lookUpOfferedPreview } = useSummaPreview();
  const { messages, handlePrompt, isLoading, error, setChatFeedback, clearChat, preview, previewRefusal, startNewConversation } = useChat(
    AssistantType.Citizen,
    citizenId,
    chosenPreview
  );
  const { draftsAvailable, showDrafts, changeShowDrafts } = useShowDrafts();
  const hasAnswers = messages.some(message => message.role === 'Assistent');
  const previewAvailable = mayTryPreview || !!preview;

  // A refusal means Ops changed what may be tried, so the switch must show what is offered now.
  useEffect(() => {
    if (previewRefusal) {
      lookUpOfferedPreview();
    }
  }, [previewRefusal]);

  function switchPreview(nextPreview?: ISummaPreview) {
    choosePreview(nextPreview);
    startNewConversation(nextPreview);
  }

  function handleSubmitPrompt(inputString: string, standardQuestionId?: string, isDraftStandardQuestion?: boolean) {
    handlePrompt({ citizenId, content: inputString.trim(), customPromptSettings: settings, standardQuestionId }, isDraftStandardQuestion);
  }

  useChatErrorHandling(error);
  useClearChatOnCitizenChange(clearChat, citizenId);

  async function handleChatFeedbackChange(msg: IChatMessage) {
    await setChatFeedback(msg);
  }

  const citizenContext = useCitizenContext();

  // Depends on toggles and rights only, so the settings button does not change meaning while a lookup is under way.
  function getSettingsPanel() {
    if (!draftsAvailable && !previewAvailable) {
      return undefined;
    }

    return {
      title: 'Summa indstillinger',
      content: (
        <SummaSettings
          draftsAvailable={draftsAvailable}
          showDrafts={showDrafts}
          onShowDraftsChange={changeShowDrafts}
          previewAvailable={previewAvailable}
          offeredPreview={offeredPreview}
          preview={preview}
          hasAnswers={hasAnswers}
          isLoading={isLoading}
          onSwitchPreview={switchPreview}
        />
      ),
    };
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', position: 'absolute', width: '100%', height: '-webkit-fill-available' }}>
      <Box pl={2}>
        <h3>
          Borgerspecifik søgning for {citizenContext.fullName} ({citizenContext.cpr})
        </h3>
      </Box>
      <SummaPreviewBanner preview={preview} refusal={previewRefusal} hasAnswers={hasAnswers} />
      <ChatMessages isLoading={isLoading} messages={messages} onChatFeedbackChange={handleChatFeedbackChange} chatComplianceBoxProps={chatComplianceBoxPropsSumma} />
      <ChatMessageInput
        isLoading={isLoading}
        onClearChat={clearChat}
        onSettingsChange={onSettingsChange}
        onSubmitPrompt={handleSubmitPrompt}
        settings={settings}
        assistantType={AssistantType.Citizen}
        dataSourcesComponent={<Checkbox label="Borgers journalnotater" checked disabled />}
        settingsPanel={getSettingsPanel()}
        showDrafts={showDrafts}
        returnedQuestion={previewRefusal}
      />
    </Box>
  );
}
